import { odontogramItems } from "./odontogram";
import type { ToothExam } from "@/types/odontogram";

/**
 * Normalised SVG primitive descriptor produced by `renderOdontogramStatus`.
 * The consumer (a Vue component template) iterates over the array and
 * renders each element with a matching `<template v-if>` branch.
 */
export type StatusElement =
  | {
      kind: "text";
      x: number;
      y: number;
      label: string;
      fontSize?: string;
      fontWeight?: string;
    }
  | {
      kind: "rect";
      x: number;
      y: number;
      width: number;
      height: number;
      fill: string;
    }
  | {
      kind: "path";
      d: string;
      fill: string;
      strokeWidth?: number;
    };

const topText = (
  item: { top: { x: string; y: string } },
  label: string,
  xOffset: number
): StatusElement => ({
  kind: "text",
  x: parseFloat(item.top.x) - xOffset,
  y: parseFloat(item.top.y) - 2,
  label,
});

const capLabel = (
  item: { cap: { x: string; y: string } },
  label: string,
  xOffset: number
): StatusElement => ({
  kind: "text",
  x: parseFloat(item.cap.x) + xOffset,
  y: parseFloat(item.cap.y) - 10,
  label,
  fontSize: "10px",
  fontWeight: "bold",
});

const topRect = (
  item: { top: { x: string; y: string } },
  fill: string
): StatusElement => ({
  kind: "rect",
  x: parseFloat(item.top.x) - 30,
  y: parseFloat(item.top.y) - 30,
  width: 62,
  height: 100,
  fill,
});

const bridgeFill = (isBridgeStart: boolean, isBridgeEnd: boolean): string =>
  isBridgeStart
    ? "url(#img-bridge-start)"
    : isBridgeEnd
      ? "url(#img-bridge-end)"
      : "url(#img-bridge-mid)";

export function renderOdontogramStatus(
  status: ToothExam,
  isBridgeStart = false,
  isBridgeEnd = false
): StatusElement[] {
  const item = odontogramItems.find((it) => it.id === status.tooth_id);

  if (!item) {
return [];
}

  const path = item.path[status.tooth_surface_id || "all"];

  if (!path) {
return [];
}

  switch (status.value_id) {
    // START OF TOOTH FINDINGS
    case "OV000090":
      return [topText(item, "NON", 17)];
    case "278658009":
      return [topText(item, "UNE", 15)];
    case "278660006":
      return [topText(item, "PRE", 13)];
    case "129263008":
      return [topText(item, "IMV", 13)];
    case "OV000091":
      return [topText(item, "ANO", 15)];
    case "734009000":
      return [topText(item, "DIA", 13)];
    case "53963006":
      return [topText(item, "ATT", 13)];
    case "47222000":
      return [topText(item, "ABR", 13)];
    case "278590005":
      return [{ kind: "path", d: path, fill: "url(#img-cfr)" }];
    case "OV000092":
      return [topRect(item, "url(#img-nvt)")];
    case "OV000093":
    case "66569006":
      return [topRect(item, "url(#img-rrx)")];
    case "234948008":
      return [topRect(item, "url(#img-mis)")];
    case "278661005":
      return [topText(item, "Pre", 10)];
    case "307084006":
      return [topText(item, "Non", 13)];
    case "397797004":
      return [topText(item, "ERU", 13)];
    case "272305001":
      return [topText(item, "PPX", 13)];
    case "70931000":
      return [topText(item, "REX", 13)];
    case "57602001":
      return [topText(item, "DEX", 13)];
    case "35452009":
      return [topText(item, "DIX", 11)];
    case "109683005":
      return [topText(item, "FRX", 13)];
    case "287451003":
      return [topText(item, "CAV", 15)];
    case "39273001":
      return [topText(item, "APP", 12)];
    case "17552000":
      return [topText(item, "CAL", 13)];
    case "82212003":
      return [topText(item, "ERO", 14)];
    case "30265004":
      return [topText(item, "FLU", 13)];
    case "252283005":
      return [topText(item, "MAP", 14)];
    case "716296001":
      return [topText(item, "PEX", 13)];
    case "699457000":
      return [topText(item, "M.Ver", 18)];
    case "699651005":
      return [topText(item, "D.Ver", 18)];
    case "109498002":
      return [topText(item, "V.Ver", 17)];
    case "109499005":
      return [topText(item, "L.Ver", 16)];
    case "OV000103":
      return [topText(item, "P.Ver", 16)];
    case "OV000104":
      return [topText(item, "ML.Ver", 22)];
    case "OV000105":
      return [topText(item, "MP.Ver", 22)];
    case "OV000106":
      return [topText(item, "DL.Ver", 22)];
    case "OV000107":
      return [topText(item, "DV.Ver", 22)];
    case "OV000108":
      return [topText(item, "DP.Ver", 22)];
    case "272274004":
      return [topText(item, "SPL", 13)];
    case "80967001":
      return [{ kind: "path", d: path, fill: "transparent", strokeWidth: 3 }];
    // END OF TOOTH FINDINGS

    // START OF TOOTH RESTORATION MATERIALS
    case "256447001":
      return [{ kind: "path", d: path, fill: "black" }];
    case "256454007":
      return [{ kind: "path", d: path, fill: "lightblue" }];
    case "256452006":
      return [{ kind: "path", d: path, fill: "url(#img-cof)" }];
    case "256455008":
      return [{ kind: "path", d: path, fill: "rgba(200,100,100,0.8)" }];
    case "278557005":
      return [{ kind: "path", d: path, fill: "grey" }];
    case "261253002":
      return [{ kind: "path", d: path, fill: "red" }];
    case "733965001":
      return [{ kind: "path", d: path, fill: "green" }];
    case "OV000114":
      return [{ kind: "path", d: path, fill: "orange" }];
    case "256511000":
      return [{ kind: "path", d: path, fill: "purple" }];
    case "OV000115":
      return [{ kind: "path", d: path, fill: "coral" }];
    case "OV000116":
      return [{ kind: "path", d: path, fill: "yellow" }];
    case "OV000117":
      return [{ kind: "path", d: path, fill: "chocolate" }];
    case "256496006":
      return [{ kind: "path", d: path, fill: "gold" }];
    case "256485003":
      return [{ kind: "path", d: path, fill: "lightpink" }];
    case "256532008":
      return [{ kind: "path", d: path, fill: "aquamarine" }];
    case "256480008":
      return [{ kind: "path", d: path, fill: "brown" }];
    // END OF TOOTH RESTORATION MATERIALS

    // START OF TOOTH RESTORATION
    case "468240009":
      return [{ kind: "path", d: path, fill: "url(#img-fmc)" }];
    case "468254001":
      return [{ kind: "path", d: path, fill: "url(#img-poc)" }];
    case "468778007":
      return [{ kind: "path", d: path, fill: "url(#img-mpc)" }];
    case "272291000":
      return [{ kind: "path", d: path, fill: "url(#img-gmc)" }];
    case "706370006":
      return [topRect(item, "url(#img-rct)")];
    case "468993001":
      return [topText(item, "IPX", 10)];
    case "OV000094":
      return [topRect(item, bridgeFill(isBridgeStart, isBridgeEnd))];
    case "OV000095":
      return [
        { kind: "path", d: path, fill: "url(#img-poc)" },
        topRect(item, bridgeFill(isBridgeStart, isBridgeEnd)),
      ];
    case "260930001":
      return [{ kind: "path", d: path, fill: "url(#img-pon)" }];
    case "707144009":
      return [{ kind: "path", d: path, fill: "url(#img-abu)" }];
    case "272287005":
      return [{ kind: "path", d: path, fill: "url(#img-inl)" }];
    case "277487004":
      return [{ kind: "path", d: path, fill: "url(#img-onl)" }];
    case "272289008":
      return [{ kind: "path", d: path, fill: "url(#img-crown)" }];
    case "272299003":
      return [{ kind: "path", d: path, fill: "url(#img-bridge)" }];
    case "278553009":
      return [{ kind: "path", d: path, fill: "url(#img-dv)" }];
    case "278550007":
      return [{ kind: "path", d: path, fill: "url(#img-df)" }];
    //  END OF TOOTH RESTORATION

    // START OF PROSTHESIS MATERIAL
    case "256525004":
      return [capLabel(item, "BMP", -10)];
    case "256504004":
      return [capLabel(item, "BPP", -10)];
    // END OF PROSTHESIS MATERIAL

    // START OF PROSTHESIS
    case "OV000110":
      return [capLabel(item, "HUD", 25)];
    case "OV000109":
      return [capLabel(item, "HLD", 25)];
    case "278426008":
      return [capLabel(item, "PUD", 25)];
    case "278428009":
      return [capLabel(item, "PLD", 25)];
    case "272254006":
      return [capLabel(item, "FUD", 25)];
    case "706359004":
      return [capLabel(item, "CLA", 25)];
    case "272253000":
      return [capLabel(item, "FLD", 25)];
    case "272256008":
      return [capLabel(item, "PRD", 25)];
    // END OF PROSTHESIS

    default:
      return [];
  }
}

/**
 * Determine whether a bridge exam (OV000094 / OV000095) is at the start
 * or the end of a contiguous run in the tooth-row layout.
 *
 * Mirrors the adjacency lookup in the React reference (odontogram.tsx:99-113).
 */
export function bridgeFlags(
  exam: ToothExam,
  exams: ToothExam[],
  numbers: number[][]
): { isBridgeStart: boolean; isBridgeEnd: boolean } {
  let leftNumber: number | null = null;
  let rightNumber: number | null = null;

  for (const row of numbers) {
    const i = row.indexOf(exam.tooth_id);

    if (i >= 0) {
      leftNumber = i > 0 ? row[i - 1] : null;
      rightNumber = i < row.length - 1 ? row[i + 1] : null;
      break;
    }
  }

  const left = exams.find((item) => item.tooth_id === leftNumber);
  const right = exams.find((item) => item.tooth_id === rightNumber);

  return {
    isBridgeStart: left?.value_id !== exam.value_id,
    isBridgeEnd: right?.value_id !== exam.value_id,
  };
}
